/** @format */

import { useFileUpload } from "./fileUpload/useFileUpload";
import { DropZone } from "./dropZone/DropZone";
import { GenericIcon } from "../genericIcon/GenericIcon";
import { Box, Typography } from "@mui/material";
import { FC, useId } from "react";

interface IProps {
  setFileHandler: (file: File | null) => void;
  extensions?: string[];
  maxFileSize?: number;
  fileType?: string;
}

const joinExtensionsLabel = (extensions: string[]) => {
  return extensions.reduce(
    (text, value, i, array) =>
      text.toUpperCase() +
      (i < array.length - 1 ? ", " : " lub ") +
      value.toUpperCase()
  );
};

export const FileUploadLayout: FC<IProps> = ({
  setFileHandler,
  fileType = "image",
  extensions = ["jpg", "png"],
  maxFileSize = 1,
}): JSX.Element => {
  const inputId = useId();
  const {
    onDragStateChange,
    onFileDrop,
    setIsDropActive,
    isDropActive,
    isFileTooBig,
    isFileNotSupported,
  } = useFileUpload(setFileHandler, maxFileSize, fileType, extensions);

  return (
    <DropZone
      onDragStateChange={onDragStateChange}
      onFileDrop={onFileDrop}
      style={{
        width: "100%",
        height: "100%",
        borderRadius: "4px",
        border: isDropActive ? "1px dashed #2196F3" : "1px dashed #e0e0e0",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      <GenericIcon iconName="UploadFile" style={{ color: "primary.light" }} />
      <Typography>
        <label
          htmlFor={inputId}
          style={{
            color: "#2196F3",
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          Wybierz
          <input
            id={inputId}
            type="file"
            accept={extensions.join(", ")}
            style={{ display: "none" }}
            onChange={(event) => {
              event.preventDefault();
              const input = event.target;
              if (input.files && input.files[0]) {
                onFileDrop(input.files[0]);
                setIsDropActive(true);
              }
              // Reset the input value so picking the SAME file again still fires
              // `onChange` (the browser skips the event when the value is unchanged).
              // Without this, re-adding a just-removed file silently does nothing.
              input.value = "";
            }}
          />
        </label>
        &nbsp;lub przeciągnij plik
      </Typography>
      <Typography fontSize="14px" sx={{ textAlign: "center", px: 1 }}>
        {isFileNotSupported ? (
          <Box component={"span"} sx={{ color: "error.main" }}>
            {`Nieobsługiwany format pliku. Dozwolone: ${joinExtensionsLabel(extensions)}`}
          </Box>
        ) : isFileTooBig ? (
          <Box component={"span"} sx={{ color: "error.main" }}>
            {`Plik jest za duży (max. ${maxFileSize * 1024}kB)`}
          </Box>
        ) : (
          <>
            <Box component={"span"} sx={{ color: "text.secondary" }}>
              {joinExtensionsLabel(extensions)}
            </Box>
            <Box
              component={"span"}
              sx={{ color: "text.secondary" }}
            >{` (max. ${maxFileSize * 1024}kB)`}</Box>
          </>
        )}
      </Typography>
    </DropZone>
  );
};
